<template>
  <div ref="chart" class="chart" :style="{ height: chartHeight + 'px' }"></div>
</template>

<script>
import * as echarts from "echarts";
export default {
  name: "sensorCharts",
  props: {
    // 图表高度
    chartHeight: {
      type: Number,
      default: 450,
    },
    initialData: {
      type: Array,
      default: () => [],
    },
    unit: {
      type: String,
      default: "",
    },
    page: {
      type: Number,
      default: 1,
    },
    limit: {
      type: Number,
      default: 50,
    },
  },
  data() {
    return {
      chart: null,
      formattedData: [], // 存储格式化后的数据
      isLoading: false,
      lastDataZoom: {
        start: 0,
        end: 100,
      },
      isDataLoaded: false,
      screenLength: 10,
    };
  },
  computed: {
    statusText() {
      if (this.isLoading) return "正在加载更多数据...";
      return `已加载 ${this.formattedData.length} 个数据点`;
    },
    dataRange() {
      if (this.formattedData.length === 0) return "无数据";
      return `${this.formattedData[0].time} 至 ${
        this.formattedData[this.formattedData.length - 1].time
      }`;
    },
    dataFormat() {
      if (this.formattedData.length === 0) return "暂无数据";

      const sampleData = [
        this.formattedData[0],
        this.formattedData[1],
        { data: "...", time: "..." },
        this.formattedData[this.formattedData.length - 1],
      ];

      return JSON.stringify(sampleData, null, 2);
    },
    // 提取时间数组用于x轴
    timeData() {
      return this.formattedData.map((item) => item.time);
    },
    // 提取数值数组用于y轴
    valueData() {
      return this.formattedData.map((item) => item.data);
    },
  },
  watch: {
    // 监听初始数据变化
    initialData: {
      immediate: true,
      handler(newVal) {
        this.formattedData = [...newVal];
        this.updateChart();
      },
    },
  },
  mounted() {
    this.initChart();
    window.addEventListener("resize", this.handleResize);
    this.screenLength = this.limit / 5;
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    // 初始化图表
    initChart() {
      this.chart = echarts.init(this.$refs.chart);
      // 配置图表选项
      const option = {
        tooltip: {
          trigger: "axis",
          position: function (pt) {
            return [pt[0], "10%"];
          },
        },
        xAxis: {
          type: "category",
          data: this.timeData,
          axisLabel: {
            align: 'center',
            color: "#7f8c8d",
            formatter: (value, index) => {
              return value.split(" ")[0] + "\n" + value.split(" ")[1];
            },
          },
        },
        yAxis: {
          type: "value",
          name: this.unit,
          axisLine: {
            lineStyle: {
              color: "#dce1e8",
            },
          },
          min: function (value) {
            return Math.floor(value.min - 1);
          },
          max: function (value) {
            return Math.ceil(value.max + 1);
          },
          axisLabel: {
            color: "#7f8c8d",
            formatter: (value, index) => {
              return Number(value.toFixed(2)) + this.unit;
              // '{value}℃'
            },
          },
        },
        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 20,
            bottom: 50
          },
          {
            start: 0,
            end: 20,
            bottom: 60
          },
        ],
        series: [
          {
            name: "传感器数据(" + this.unit + ")",
            data: this.valueData,
            type: "line",
            smooth: true
          },
        ],
      };

      this.chart.setOption(option);

      // 监听dataZoom事件
      this.chart.on("dataZoom", this.handleDataZoom);
    },

    // 处理dataZoom事件
    handleDataZoom(event) {
      if (event.end) {
        this.screenLength = Math.ceil(
          ((event.end - event.start) * this.formattedData.length) / 100
        );
        if (event.end >= 99.9 && !this.isDataLoaded) {
          this.isDataLoaded = true;
          this.loadMoreData();
        }
      } else {
        this.screenLength = Math.ceil(
          ((event.batch[0].end - event.batch[0].start) *
            this.formattedData.length) /
            100
        );
        if (event.batch[0].end >= 99.9 && !this.isDataLoaded) {
          this.isDataLoaded = true;
          this.loadMoreData();
        }
      }
    },

    // 加载更多数据
    loadMoreData() {
      if (this.isLoading) return;

      this.isLoading = true;

      // 向父组件请求更多数据
      this.$emit("load-more");
    },

    // 更新图表数据
    updateChartData(newData) {
      // 合并新数据
      this.formattedData = [...this.formattedData, ...newData];
      // 更新图表
      this.updateChart();
    },

    // 更新图表
    updateChart() {
      //   console.log(this.timeData, this.valueData);
      if (!this.chart) return;

      this.chart.setOption({
        xAxis: {
          data: this.timeData,
        },
        series: [
          {
            data: this.valueData,
          },
        ],
      });
      if (this.page == 1) {
        this.chart.setOption({
          dataZoom: [
            {
              start: 0,
              end: 20,
            },
          ],
        });
      } else {
        this.chart.setOption({
          dataZoom: [
            {
              start:
                ((this.formattedData.length - this.limit - this.screenLength) /
                  this.formattedData.length) *
                100,
              end: ((this.page - 1) / this.page) * 100,
            },
          ],
        });
      }

      //  start: 0,
      //  end: 20,
      this.isDataLoaded = false;
      // 重置加载状态
      this.isLoading = false;
    },

    // 重置数据
    resetData() {
      this.formattedData = [...this.initialData];
      this.updateChart();
      this.lastDataZoom = { start: 80, end: 100 };
    },
    // 处理窗口大小变化
    handleResize() {
      if (this.chart) {
        this.chart.resize();
      }
    },
  },
};
</script>

<style>
</style>